// @ts-nocheck
/**
 * Helpers for TemplateDocument storage and UI (replaces ContaindTemplate accessors).
 */

import {
  countDistinctLocales,
  getLocalizedTextLocales,
  resolveLocalizedText,
} from '../i18n/resolveLocalizedText';
import type { ElementDefinition, ElementLayout, LocalizedText, TemplateDocument } from '../v2/types/canonical';
import type { TemplateStorageMetadata } from '../types/TemplateStorageTypes';
import {
  isDefaultElementId,
  isDefaultLikeTemplateId,
  normalizeTemplateElementIdForLookup,
} from './TemplateIdUtils';

export function getTemplateId(doc: TemplateDocument): string {
  return doc.metadata.templateId;
}

export function getTemplateDisplayName(doc: TemplateDocument, locale = 'en'): string {
  const resolved = resolveLocalizedText(doc.metadata.displayName, locale, {
    fallback: doc.metadata.templateId,
  });
  return resolved || doc.metadata.templateId;
}

export function getTemplateDescription(doc: TemplateDocument, locale = 'en'): string {
  return resolveLocalizedText(doc.metadata.description, locale);
}

function collectDocumentLocalizedFields(doc: TemplateDocument): LocalizedText[] {
  const values: LocalizedText[] = [doc.metadata.displayName];
  if (doc.metadata.description) values.push(doc.metadata.description);

  for (const element of doc.elements) {
    values.push(element.displayName);
    if (element.description) values.push(element.description);
    for (const layout of element.layouts) {
      values.push(layout.displayName);
    }
  }
  for (const bundle of doc.categories) {
    if (bundle.displayName) values.push(bundle.displayName);
    for (const category of bundle.categories) {
      values.push(category.name);
      if (category.description) values.push(category.description);
    }
    for (const property of bundle.properties) {
      values.push(property.name);
      if (property.description) values.push(property.description);
      for (const enumValue of property.validation?.enumValues ?? []) {
        values.push(enumValue.displayName);
      }
      if (property.uiHints?.placeholder) values.push(property.uiHints.placeholder);
      if (property.uiHints?.helpText) values.push(property.uiHints.helpText);
    }
  }
  return values;
}

export function getTemplateLanguageCount(doc: TemplateDocument): number {
  return countDistinctLocales(...collectDocumentLocalizedFields(doc));
}

export function getTemplateAvailableLocales(doc: TemplateDocument): string[] {
  const locales = new Set<string>();
  for (const value of collectDocumentLocalizedFields(doc)) {
    for (const locale of getLocalizedTextLocales(value)) {
      locales.add(locale);
    }
  }
  return [...locales].sort();
}

export function templateDocumentToStorageMetadata(
  doc: TemplateDocument,
  filename: string,
  fileSize: number,
  options?: { isPredefined?: boolean },
): TemplateStorageMetadata {
  return {
    themeId: doc.metadata.templateId,
    name: getTemplateDisplayName(doc),
    description: getTemplateDescription(doc),
    filename,
    fileSize,
    languageCount: getTemplateLanguageCount(doc),
    createdBy: doc.metadata.author,
    creationTimestamp: doc.metadata.createdAt
      ? Date.parse(doc.metadata.createdAt)
      : undefined,
    topLevelMultiLayoutId: doc.metadata.primaryElementId,
    lastModified: Date.now(),
    isPredefined: options?.isPredefined,
  };
}

export function findElementById(doc: TemplateDocument, elementId: string): ElementDefinition | undefined {
  return doc.elements.find((el) => el.id === elementId);
}

/** Resolve an item's template element, including legacy id normalization and fallbacks. */
export function resolveElementForTemplate(
  doc: TemplateDocument,
  elementId?: string | null,
): ElementDefinition | undefined {
  const elements = doc.elements ?? [];
  if (elements.length === 0) {
    return undefined;
  }

  // Themed list-root generic sentinel: do not fall through to primary/elements[0].
  if (isDefaultElementId(elementId) && !isDefaultLikeTemplateId(doc.metadata.templateId)) {
    return undefined;
  }

  const lookupId = normalizeTemplateElementIdForLookup(elementId);
  const direct = elements.find((el) => el.id === lookupId);
  if (direct) {
    return direct;
  }

  const primaryId = doc.metadata.primaryElementId;
  if (primaryId) {
    const primary = elements.find((el) => el.id === primaryId);
    if (primary) {
      return primary;
    }
  }

  return elements[0];
}

export function getElementDisplayName(element: ElementDefinition, locale = 'en'): string {
  return resolveLocalizedText(element.displayName, locale, { fallback: element.id }) || element.id;
}

export function getElementDescription(element: ElementDefinition, locale = 'en'): string {
  return resolveLocalizedText(element.description, locale, { fallback: '' });
}

/** All layout variants in template definition order (includes placeholder layouts). */
export function getOrderedElementLayouts(
  element: ElementDefinition | undefined,
): ElementLayout[] {
  return element?.layouts ?? [];
}

/** True when a layout is the element's placeholder-only layout (DEC-062). */
export function isPlaceholderLayout(layout: ElementLayout | undefined): boolean {
  return layout?.isPlaceholderLayout === true;
}

/**
 * Layouts that participate in layoutInUse / canvas +/− cycling.
 * Placeholder layouts are excluded so adding one never shifts indices (DEC-062).
 */
export function getCyclableLayouts(
  element: ElementDefinition | undefined,
): ElementLayout[] {
  return getOrderedElementLayouts(element).filter((layout) => !isPlaceholderLayout(layout));
}

/** The at-most-one placeholder layout for an element, if authored. */
export function getPlaceholderLayout(
  element: ElementDefinition | undefined,
): ElementLayout | undefined {
  return getOrderedElementLayouts(element).find(isPlaceholderLayout);
}

/** Number of layouts the user can cycle through on canvas (+/− controls). */
export function getCyclableLayoutCount(element: ElementDefinition | undefined): number {
  return getCyclableLayouts(element).length;
}

/** @deprecated Use getCyclableLayoutCount — same semantics. */
export function getElementLayoutCount(element: ElementDefinition | undefined): number {
  return getCyclableLayoutCount(element);
}

export function resolveActiveLayoutId(
  element: ElementDefinition | undefined,
  layoutIndex: number,
): string | undefined {
  const layouts = getCyclableLayouts(element);
  if (layouts.length === 0) {
    return undefined;
  }
  const bounded = Math.max(0, Math.min(layoutIndex, layouts.length - 1));
  return layouts[bounded]?.id;
}

/** Alias for resolveActiveLayoutId — index in cyclable order → layout id. */
export function resolveLayoutIndexToId(
  element: ElementDefinition | undefined,
  layoutIndex: number,
): string | undefined {
  return resolveActiveLayoutId(element, layoutIndex);
}

/** Inverse of resolveLayoutIndexToId; returns -1 when layout id is not cyclable / not found. */
export function resolveLayoutIdToIndex(
  element: ElementDefinition | undefined,
  layoutId: string,
): number {
  const layouts = getCyclableLayouts(element);
  const index = layouts.findIndex((layout) => layout.id === layoutId);
  return index;
}

export function getLayoutDisplayName(
  layout: ElementLayout | undefined,
  locale = 'en',
): string {
  if (!layout) return '';
  return resolveLocalizedText(layout.displayName, locale, { fallback: layout.id }) || layout.id;
}

/** Layout at cyclable index (bounded). Placeholder layouts are not addressable. */
export function getLayoutAtIndex(
  element: ElementDefinition | undefined,
  layoutIndex: number,
): ElementLayout | undefined {
  const layouts = getCyclableLayouts(element);
  if (layouts.length === 0) {
    return undefined;
  }
  const bounded = Math.max(0, Math.min(layoutIndex, layouts.length - 1));
  return layouts[bounded];
}

export function isRenderAsBuiltinLayout(layout: ElementLayout | undefined): boolean {
  return layout?.renderAsBuiltin === true;
}

/** True when a layout can be rendered (builtin or has components). */
export function isLayoutRenderable(layout: ElementLayout): boolean {
  return layout.renderAsBuiltin === true || (layout.components?.length ?? 0) > 0;
}

export function hasAnyRenderableLayout(element: ElementDefinition | undefined): boolean {
  return getCyclableLayouts(element).some(isLayoutRenderable);
}

/** First renderable cyclable index; 0 if none (authoring WIP). */
export function resolveFirstRenderableLayoutIndex(element: ElementDefinition | undefined): number {
  const layouts = getCyclableLayouts(element);
  const idx = layouts.findIndex(isLayoutRenderable);
  return idx >= 0 ? idx : 0;
}

export function isTemplateDocument(value: unknown): value is TemplateDocument {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<TemplateDocument>;
  return candidate.kind === 'template' && candidate.version === 2 && Array.isArray(candidate.elements);
}

/** True when the element defines pipeline-evaluated field expression bindings. */
export function elementHasFieldBindings(element: ElementDefinition | undefined): boolean {
  return (element?.scripts?.fieldBindings?.bindings?.length ?? 0) > 0;
}
